import { useDark } from '@vueuse/core'
import type { ActiveElement, ChartData, ChartDataset, TooltipCallbacks } from 'chart.js'
import { computed, type ComputedRef } from 'vue'

/**
 * Common type for our charts' computed datasource
 */
type CommonChartData = ChartData<'bar' | 'doughnut', number[], string>

export type BarchartDataset = ChartDataset<'bar', number[]>
export type DoughnutDataset = ChartDataset<'doughnut', number[]>

/**
 * Normalizes a dataset to the 0-100 range (so datasets with diverse total units amounts line up)
 * @param baseData the dataset array
 * @returns the dataset array passed in, with each of their `.data` props updated to normalized values
 */
export const normalizeDataset = <T extends BarchartDataset | DoughnutDataset>(baseData: T[]) =>
  baseData.map((dataset) => ({
    ...dataset,
    data: dataset.data.map((value, index) => {
      const total = baseData.reduce((sum, d) => sum + d.data[index], 0)
      return (value / total) * 100
    }),
  }))

/* Dataset settings (mostly styles) */

const isDark = useDark()

const borderRadius = 5

/** Dataset settings common for all datasets */
export const baseDatasetSettings = computed<Partial<BarchartDataset & DoughnutDataset>>(() => ({
  // When the border radius is supplied as a number and the chart is stacked, the radius will only be applied to the
  // bars that are at the edges of the stack or where the bar is floating. The object syntax can be used to override this behavior.
  borderRadius: {
    topLeft: borderRadius,
    bottomLeft: borderRadius,
    topRight: borderRadius,
    bottomRight: borderRadius,
  },
  borderColor: isDark.value ? '#1a1a1a' : '#f0f0f0',
  borderWidth: 2,
  hoverBorderWidth: 0,
}))

/** Dataset settings common for all barchart datasets */
export const baseBarchartDatasetSettings = computed<Partial<BarchartDataset>>(() => ({
  ...baseDatasetSettings.value,
  barThickness: 40,
  indexAxis: 'y',
  borderSkipped: false,
}))

/** Dataset settings common for all doughnut datasets */
export const baseDoughnutDatasetSettings = computed<Partial<DoughnutDataset>>(() => ({
  ...baseDatasetSettings.value,
  hoverOffset: 8,
  borderRadius,
  borderColor: isDark.value ? '#1a1a1a' : '#f0f0f0',
}))

/* Event listeners */

/**
 * Retrieves the datum from the source dataset after a Chartjs element click event.
 * ChartEvent pipes the _indexes_ of the dataset and the datum within it.
 *
 * @param chartData the computed() chart data
 * @returns event payload with id (dataset + category) of clicked element
 */
export const retrieveClickedChartDatum = <T extends CommonChartData>(
  chartData: ComputedRef<T>,
  elements: ActiveElement[]
) => {
  if (elements.length > 0) {
    const element = elements[0]
    const { datasetIndex, index } = element
    const dataset = chartData.value.datasets[datasetIndex]

    const label = chartData.value.labels?.[index] ?? 'UNLABELED'

    return {
      dataset: dataset.label,
      category: label,
    }
  }
  return { dataset: null, category: null }
}

export type ChartClickEventDatum = ReturnType<typeof retrieveClickedChartDatum>

/**
 * Common tooltip label event listener. Gets a chart's data (i.e. an array of datasets)
 * and returns an event listener that'd form the tooltip's message from the hover event context.
 */
export const tooltipStatsLabelFor =
  (baseData: ComputedRef<CommonChartData['datasets']>): TooltipCallbacks<'bar' | 'doughnut'>['label'] =>
  (context) => {
    // Get total for this _category_ from dataset, as computed values are percentiles
    const unitsTotal = baseData.value.reduce((sum, dataset) => sum + dataset.data[context.dataIndex], 0)
    const unitsValue = baseData.value[context.datasetIndex].data[context.dataIndex]

    const percentage = ((100 * unitsValue) / unitsTotal).toFixed(0)
    return `${context.dataset.label}: ${unitsValue}/${unitsTotal} ~ ${percentage}%`
  }

/**
 * Get a css's variable `rgb(r, g, b)`-formatted color string from its variable name
 * Required to pass color values into canvas-rendered chartjs chart, since it doesn't support CSS vars out-of-the-box
 * @param variableName color variable name e.g. `--color-sprout-400`.
 * @returns `rgb(r, g, b)`-formatted color string e.g. `rgb(129, 183, 100)`
 */
export const getCSSVariableColor = (variableName: string): string => {
  const rawTriplet = getComputedStyle(document.documentElement).getPropertyValue(variableName).trim()
  return `rgb(${rawTriplet})`
}

// Labels and datasets on our charts.
//
// These are the source of truth for the labels and categories expected to be implemented on the
// charts themselves and for the possible values that the click event handlers should expect:

export interface MinersStatusChartConf {
  label: 'Good' | 'Fail' | 'Unset'
  category: 'Inventoried' | 'Cleaned' | 'Initial Diagnostic' | 'Stress Tested'
}

export interface PartsStatusChartConf {
  label: 'PSU' | 'CB' | 'Fans' | 'HB'
  category:
    | 'Good'
    | 'Repaired/Replaced'
    | 'Fail'
    | 'Blocked'
    | 'Repair not authorized'
    | 'Unrepairable/Irreparable'
    | 'Undiagnosed'
}

export interface ServicesChartConf {
  label: string
  category: 'Performed' | 'Not performed'
}
